import React from "react";
import { createPortal } from "react-dom";
import {
  Check,
  ChevronDown,
  LayoutDashboard,
  Loader2,
  Package,
  PackageSearch,
  PlugZap,
  SquareChartGantt,
  Waypoints,
  X,
  Zap,
} from "lucide-react";
import { cn } from "../../../../lib/utils";

const ProductListPortal = ({ children }) => {
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  if (!mounted || typeof document === "undefined") {
    return null;
  }

  return createPortal(children, document.body);
};

const useOverlayBehavior = (open, onClose) => {
  React.useEffect(() => {
    if (!open || typeof document === "undefined") {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);
};

export const SpinnerIcon = ({ className }) => (
  <Loader2 className={cn("h-4 w-4 animate-spin", className)} />
);

export const StatusTag = ({ tone = "neutral", className, children }) => {
  const toneClasses = {
    error:
      "bg-rose-500/10 text-rose-700 ring-1 ring-inset ring-rose-500/15 backdrop-blur-sm dark:text-rose-300",
    success:
      "bg-emerald-500/10 text-emerald-700 shadow-[0_0_0_1px_rgba(16,185,129,0.16),0_0_18px_rgba(16,185,129,0.08)] backdrop-blur-sm dark:text-emerald-300",
    neutral:
      "bg-muted/70 text-muted-foreground ring-1 ring-inset ring-border/60 backdrop-blur-sm",
  };

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-xl px-2.5 py-1 text-[11px] font-semibold leading-none tracking-[0.01em]",
        toneClasses[tone] ?? toneClasses.neutral,
        className,
      )}
    >
      {children}
    </span>
  );
};

export const ProductButton = React.forwardRef(
  (
    {
      className,
      variant = "solid",
      size = "default",
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const variantClasses = {
      solid:
        "bg-foreground text-background shadow-[0_8px_22px_-16px_rgba(0,0,0,0.7)] hover:bg-foreground/92 disabled:bg-muted disabled:text-muted-foreground",
      outline:
        "border border-border/70 bg-background/60 text-foreground hover:border-border hover:bg-accent/35 disabled:border-border/50 disabled:bg-muted/40 disabled:text-muted-foreground",
      ghost:
        "bg-transparent text-muted-foreground hover:bg-accent/30 hover:text-foreground",
    };

    const sizeClasses = {
      default: "min-h-10 px-4 py-2 text-sm",
      sm: "min-h-9 px-3.5 py-2 text-[13px]",
      lg: "min-h-10 px-[17px] py-2 text-[13px]",
      icon: "h-9 w-9 p-0",
    };

    return (
      <button
        ref={ref}
        type="button"
        disabled={disabled}
        className={cn(
          "inline-flex items-center justify-center gap-2 rounded-xl font-medium transition-[background-color,border-color,transform,opacity,color,box-shadow] duration-200 ease-out hover:scale-[1.01] active:scale-[0.99] disabled:pointer-events-none disabled:scale-100",
          variantClasses[variant],
          sizeClasses[size],
          className,
        )}
        {...props}
      >
        {children}
      </button>
    );
  },
);

ProductButton.displayName = "ProductButton";

const PRODUCT_VISUALS = [
  {
    match: ["dashboard"],
    icon: LayoutDashboard,
    className:
      "bg-cyan-500/10 text-cyan-600 ring-1 ring-inset ring-cyan-500/15 dark:text-cyan-300",
  },
  {
    match: ["board", "kanban", "jboard"],
    icon: SquareChartGantt,
    className:
      "bg-violet-500/10 text-violet-600 ring-1 ring-inset ring-violet-500/15 dark:text-violet-300",
  },
  {
    match: ["rf", "radio"],
    icon: Waypoints,
    className:
      "bg-amber-500/10 text-amber-600 ring-1 ring-inset ring-amber-500/15 dark:text-amber-300",
  },
  {
    match: ["moca", "client", "connect"],
    icon: PlugZap,
    className:
      "bg-blue-500/10 text-blue-600 ring-1 ring-inset ring-blue-500/15 dark:text-blue-300",
  },
  {
    match: ["oogy", "auto", "smart"],
    icon: Zap,
    className:
      "bg-emerald-500/10 text-emerald-600 ring-1 ring-inset ring-emerald-500/15 dark:text-emerald-300",
  },
];

export const getProductVisual = (displayName = "") => {
  const normalizedName = displayName.toLowerCase();

  return (
    PRODUCT_VISUALS.find(({ match }) =>
      match.some((token) => normalizedName.includes(token)),
    ) ?? {
      icon: Package,
      className:
        "bg-muted/70 text-muted-foreground ring-1 ring-inset ring-border/60",
    }
  );
};

export const ProductIconBadge = ({ productName, className }) => {
  const visual = getProductVisual(productName);
  const Icon = visual.icon;

  return (
    <div
      className={cn(
        "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-transform duration-200 group-hover/product:scale-[1.02]",
        visual.className,
        className,
      )}
    >
      <Icon className="h-[18px] w-[18px]" />
    </div>
  );
};

export const ProductMetaDot = () => (
  <span className="h-1 w-1 rounded-full bg-border/80" />
);

export const ProductSummaryStrip = ({ items = [], className }) => (
  <div
    className={cn(
      "mb-2 flex flex-wrap items-center gap-x-6 gap-y-4 border-b border-border/60 pb-4",
      className,
    )}
  >
    {items.map((item, index) => (
      <React.Fragment key={item.label}>
        <div className="min-w-[108px]">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            {item.label}
          </div>
          <div className="mt-1 text-[24px] font-semibold tracking-[-0.03em] text-foreground">
            {item.value}
          </div>
          {item.hint ? (
            <div className="mt-1 text-[13px] font-medium text-muted-foreground">
              {item.hint}
            </div>
          ) : null}
        </div>
        {index < items.length - 1 ? (
          <div className="hidden h-10 w-px bg-border/60 sm:block" />
        ) : null}
      </React.Fragment>
    ))}
  </div>
);

export const ProductEmptyState = ({ title, description, action }) => (
  <div className="flex flex-col items-center justify-center gap-3 py-14 text-center">
    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-muted/40 text-muted-foreground ring-1 ring-inset ring-border/60">
      <PackageSearch className="h-5 w-5" />
    </div>
    <div className="space-y-1">
      <h3 className="text-base font-semibold text-foreground">{title}</h3>
      <p className="max-w-md text-sm text-muted-foreground">{description}</p>
    </div>
    {action ? <div className="pt-1">{action}</div> : null}
  </div>
);

export const ProductCheckbox = ({
  checked,
  onCheckedChange,
  label,
  description,
  error,
  className,
}) => (
  <label
    className={cn(
      "flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-3 text-sm transition-colors",
      error
        ? "border-destructive/45 bg-destructive/5"
        : "border-border/60 bg-muted/20 hover:bg-muted/35",
      className,
    )}
  >
    <span className="relative mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onCheckedChange?.(event.target.checked)}
        className="peer sr-only"
      />
      <span
        className={cn(
          "flex h-4 w-4 items-center justify-center rounded-[4px] border transition-colors",
          checked
            ? "border-foreground bg-foreground text-background"
            : "border-border bg-background text-transparent",
        )}
      >
        <Check className="h-3 w-3" />
      </span>
    </span>
    <span className="space-y-1">
      <span className="block font-medium text-foreground">{label}</span>
      {description ? (
        <span className="block text-xs text-muted-foreground">
          {description}
        </span>
      ) : null}
      {error ? (
        <span className="block text-xs text-destructive">
          Please confirm before continuing.
        </span>
      ) : null}
    </span>
  </label>
);

const OverlayShell = ({
  open,
  onClose,
  children,
  align = "center",
  panelClassName,
}) => {
  useOverlayBehavior(open, onClose);

  if (!open) {
    return null;
  }

  return (
    <ProductListPortal>
      <div className="fixed inset-0 z-[1200]">
        <button
          type="button"
          aria-label="Close overlay"
          className="absolute inset-0 bg-black/45 backdrop-blur-[2px]"
          onClick={onClose}
        />
        <div
          className={cn(
            "relative flex min-h-full w-full",
            align === "end" ? "justify-end" : "items-center justify-center p-4",
          )}
        >
          <div
            role="dialog"
            aria-modal="true"
            className={cn(
              "relative z-[1] w-full border border-border/70 bg-background text-foreground shadow-2xl",
              align === "end"
                ? "ml-auto h-full max-w-xl rounded-none"
                : "max-w-lg rounded-2xl",
              panelClassName,
            )}
            onClick={(event) => event.stopPropagation()}
          >
            {children}
          </div>
        </div>
      </div>
    </ProductListPortal>
  );
};

export const ProductDialog = ({
  open,
  onClose,
  title,
  description,
  children,
  footer,
}) => (
  <OverlayShell open={open} onClose={onClose} align="center">
    <div className="space-y-5 p-6">
      <div className="space-y-1">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-foreground">{title}</h2>
            {description ? (
              <p className="text-sm text-muted-foreground">{description}</p>
            ) : null}
          </div>
          <ProductButton
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-lg text-muted-foreground"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </ProductButton>
        </div>
      </div>
      {children}
      {footer ? (
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          {footer}
        </div>
      ) : null}
    </div>
  </OverlayShell>
);

export const ProductSheet = ({
  open,
  onClose,
  title,
  description,
  children,
  footer,
}) => (
  <OverlayShell
    open={open}
    onClose={onClose}
    align="end"
    panelClassName="max-w-[min(100%,34rem)]"
  >
    <div className="flex h-full flex-col">
      <div className="flex items-start justify-between gap-4 border-b border-border/60 px-6 py-5">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold text-foreground">{title}</h2>
          {description ? (
            <p className="text-sm text-muted-foreground">{description}</p>
          ) : null}
        </div>
        <ProductButton
          variant="ghost"
          size="icon"
          className="h-9 w-9 rounded-lg text-muted-foreground"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </ProductButton>
      </div>
      <div className="app-scrollbar flex-1 overflow-y-auto px-6 py-5">
        {children}
      </div>
      {footer ? (
        <div className="border-t border-border/60 px-6 py-4">
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            {footer}
          </div>
        </div>
      ) : null}
    </div>
  </OverlayShell>
);

export const ProductAccordionItem = ({
  open,
  onToggle,
  title,
  action,
  children,
  className,
}) => {
  const contentRef = React.useRef(null);
  const [contentHeight, setContentHeight] = React.useState(0);

  React.useEffect(() => {
    if (!contentRef.current) {
      return;
    }

    setContentHeight(contentRef.current.scrollHeight);
  }, [children, open]);

  return (
    <section
      className={cn("group/product border-b border-border/60 rounded-lg", className)}
    >
      <div className="-mx-2 rounded-2xl px-2 transition-[background-color,box-shadow] duration-200 ease-out hover:bg-accent/[0.18] hover:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.03)]">
        <div className="flex min-h-[68px] flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            className="flex min-w-0 flex-1 items-center justify-between gap-4 text-left"
            onClick={onToggle}
            aria-expanded={open}
          >
            <div className="min-w-0 flex-1">{title}</div>
            <ChevronDown
              className={cn(
                "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-300 ease-out",
                open && "rotate-180",
              )}
            />
          </button>
          {action ? <div className="shrink-0">{action}</div> : null}
        </div>
      </div>

      <div
        className={cn(
          "grid transition-[grid-template-rows,opacity] duration-300 ease-in-out",
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
        )}
      >
        <div className="overflow-hidden">
          <div
            ref={contentRef}
            style={{
              maxHeight: open ? `${contentHeight}px` : "0px",
            }}
            className={cn(
              "overflow-hidden transition-[max-height,transform,opacity,padding] duration-300 ease-in-out",
              open
                ? "translate-y-0 pb-4 opacity-100"
                : "-translate-y-1 pb-0 opacity-0",
            )}
          >
            <div className="pt-1">{children}</div>
          </div>
        </div>
      </div>
    </section>
  );
};
